import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import ApiInspector from '../../../components/ApiInspector';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { formatNumber } from '../../../utils/formatters';
import { isTipologiaMerce } from '../../../utils/tipologieArticolo';
import { API_DOCS } from './ArticoliPreconfiguratiPage.api';
import './ArticoliPreconfiguratiPage.css';

// ── Icone ─────────────────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface ArticoloRow {
  id: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  nComponenti: number;
}

interface ComponenteLocale {
  _key: string;
  id?: number;
  ordine: number;
  tipologia: string;
  categoriaMerceologicaId: number | null;
  categoriaNome: string;
  articoloId: number | null;
  articoloCodice: string;
  articoloDescrizione: string;
  articoloUmVenditaSimbolo: string;
  haFormato: boolean;
  formatoId: number | null;
  formatoNome: string | null;
  quantita: number | null;
  sfrido: number | null;
  isPrincipale: boolean;
  isInDescrizione: boolean;
}

interface ArticoloDettaglio {
  id: number;
  societaId: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  componenti: ComponenteLocale[];
}

// Bozza in memoria per la creazione di un nuovo articolo pre-configurato
// (anche da duplicazione). Non corrisponde a un record sul DB: viene
// persistito solo al click su "Salva articolo".
interface DraftPreconfigurato {
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  componenti: ComponenteLocale[];
}

interface ArticoloPadreOption {
  id: number;
  codice: string;
  descrizioneBase: string;
  umVenditaSimbolo: string;
}

interface ArticoloCercaOption {
  id: number;
  codice: string;
  descrizioneBase: string;
  umVenditaSimbolo: string;
  formatoId: number | null;
  formatoNome: string | null;
  haFormato: boolean;
}

interface ModelloOption {
  id: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  descrizioneTemplate: string;
  componenti: {
    ordine: number;
    tipologia: string;
    categoriaMerceologicaId: number | null;
    categoriaNome: string;
    quantita: number | null;
    isPrincipale: boolean;
    isInDescrizione: boolean;
  }[];
}

interface SfridoRecord {
  id: number;
  quantitaDa: number;
  quantitaA: number | null;
  sfrido: number;
}

// ── Utilità ───────────────────────────────────────────────────────────────────

function risolviTemplate(template: string, componenti: { ordine: number; articoloDescrizione?: string; categoriaNome?: string }[]): string {
  return template.replace(/\{componente_(\d+)\}/g, (_, n) => {
    const c = componenti.find(x => x.ordine === Number(n));
    return c?.articoloDescrizione || c?.categoriaNome || `{componente_${n}}`;
  });
}

// ── Tooltip ────────────────────────────────────────────────────────────────────
function Tooltip({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="prec-tooltip" onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      <InfoIcon />
      {visible && (
        <span className="prec-tooltip__popup">
          {text}
          <span className="prec-tooltip__arrow" />
        </span>
      )}
    </span>
  );
}

// ── Autocomplete Articolo Padre ────────────────────────────────────────────────
function AutocompleteArticoloPadre({
  societaId, selected, onSelect, onClear,
}: {
  societaId: string;
  selected: ArticoloPadreOption | null;
  onSelect: (a: ArticoloPadreOption) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArticoloPadreOption[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    try {
      const params = new URLSearchParams({ q });
      if (societaId) params.set('societaId', societaId);
      // @api GET /api/articoli-preconfigurati/articoli-padre?societaId&q -> [{id,codice,descrizioneBase,umVenditaSimbolo}]
      const res = await apiFetch<{ success: boolean; data: ArticoloPadreOption[] }>(
        `/api/articoli-preconfigurati/articoli-padre?${params}`
      );
      setResults(res.data || []);
      setOpen(true);
    } catch { setResults([]); }
  }, [societaId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  if (selected) {
    return (
      <div className="prec-autocomplete">
        <span className="prec-selected-badge">
          <strong>{selected.codice}</strong> — {selected.descrizioneBase}
          <button className="prec-selected-badge__remove" onClick={onClear} title="Rimuovi">✕</button>
        </span>
      </div>
    );
  }

  return (
    <div className="prec-autocomplete">
      <input
        className="bop-field__input"
        value={query}
        onChange={handleChange}
        placeholder="Cerca per codice o descrizione (solo PRODOTTO_CONFIGURATO)..."
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && results.length > 0 && (
        <div className="prec-autocomplete__dropdown">
          {results.map(a => (
            <div key={a.id} className="prec-autocomplete__item" onMouseDown={() => { onSelect(a); setQuery(''); setOpen(false); }}>
              <div className="prec-autocomplete__item-code">{a.codice}</div>
              <div className="prec-autocomplete__item-desc">{a.descrizioneBase}</div>
              <div className="prec-autocomplete__item-um">UM: {a.umVenditaSimbolo}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Modale Scelta Articolo ────────────────────────────────────────────────────
function ModalSceltaArticolo({
  societaId, tipologia, categoriaMerceologicaId, categoriaNome,
  onClose, onSeleziona,
}: {
  societaId: string;
  tipologia: string;
  categoriaMerceologicaId: number | null;
  categoriaNome: string;
  onClose: () => void;
  onSeleziona: (a: ArticoloCercaOption) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArticoloCercaOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const cerca = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaId) params.set('societaId', societaId);
      if (tipologia) params.set('tipologia', tipologia);
      if (categoriaMerceologicaId) params.set('categoriaMerceologicaId', String(categoriaMerceologicaId));
      if (q) params.set('q', q);
      // @api GET /api/articoli-preconfigurati/cerca-articoli?societaId&tipologia&categoriaMerceologicaId&q -> [{id,codice,descrizioneBase,umVenditaSimbolo,formatoId,formatoNome,haFormato}]
      const res = await apiFetch<{ success: boolean; data: ArticoloCercaOption[] }>(
        `/api/articoli-preconfigurati/cerca-articoli?${params}`
      );
      setResults(res.data || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, [societaId, tipologia, categoriaMerceologicaId]);

  useEffect(() => { cerca(''); }, [cerca]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => cerca(v), 300);
  };

  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--lg" onClick={e => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">
            Scegli articolo — {categoriaNome || tipologia}
            {tipologia && <span className="bop-badge bop-badge--info" style={{ marginLeft: 'var(--bop-spacing-2)', fontSize: 'var(--bop-font-xs)' }}>{tipologia.replace(/_/g, ' ')}</span>}
          </h2>
          <button className="bop-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="bop-modal__body">
          <div className="bop-field">
            <label className="bop-field__label">Cerca articolo</label>
            <input
              className="bop-field__input"
              placeholder="Codice o descrizione..."
              value={query}
              onChange={handleQueryChange}
              autoFocus
            />
          </div>
          <div className="bop-table-wrapper" style={{ marginTop: 'var(--bop-spacing-3)' }}>
            <table className="bop-table">
              <thead>
                <tr>
                  <th className="bop-table__th">Codice</th>
                  <th className="bop-table__th">Descrizione</th>
                  <th className="bop-table__th">UM</th>
                  <th className="bop-table__th">Formato</th>
                  <th className="bop-table__th bop-table__th--actions">Azione</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="bop-table__empty"><div className="bop-empty-state"><p className="bop-empty-state__text">Caricamento...</p></div></td></tr>
                ) : results.length === 0 ? (
                  <tr><td colSpan={5} className="bop-table__empty"><div className="bop-empty-state"><span className="bop-empty-state__icon">🔍</span><p className="bop-empty-state__text">Nessun articolo trovato per tipologia «{tipologia.replace(/_/g, ' ')}»{categoriaNome ? ` e categoria «${categoriaNome}»` : ''}</p></div></td></tr>
                ) : results.map(a => (
                  <tr key={a.id} className="bop-table__row">
                    <td className="bop-table__td"><span className="bop-badge bop-badge--info">{a.codice}</span></td>
                    <td className="bop-table__td">{a.descrizioneBase}</td>
                    <td className="bop-table__td"><span className="bop-badge bop-badge--neutral">{a.umVenditaSimbolo}</span></td>
                    <td className="bop-table__td">{a.formatoNome ? <span className="bop-badge bop-badge--neutral">{a.formatoNome}</span> : <span className="bop-text-muted">—</span>}</td>
                    <td className="bop-table__td bop-table__td--actions">
                      <button className="bop-btn bop-btn-primary bop-btn-sm" onClick={() => onSeleziona(a)}>Seleziona</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale Sfrido Formato ─────────────────────────────────────────────────────
function ModalSfridoFormato({
  societaId, formatoId, formatoNome, sfridoCorrente,
  onClose, onSeleziona,
}: {
  societaId: string;
  formatoId: number;
  formatoNome: string;
  sfridoCorrente: number | null;
  onClose: () => void;
  onSeleziona: (s: number) => void;
}) {
  const [sfridi, setSfridi] = useState<SfridoRecord[]>([]);
  const [loading, setLoading] = useState(true);
  // Un errore di caricamento (es. permesso) non va confuso con "nessuno sfrido"
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const params = new URLSearchParams({ formatoId: String(formatoId) });
        if (societaId) params.set('societaId', societaId);
        // @api GET /api/articoli-preconfigurati/sfridi-formato?societaId&formatoId -> [{id,quantitaDa,quantitaA,sfrido}]
        const res = await apiFetch<{ success: boolean; data: SfridoRecord[] }>(
          `/api/articoli-preconfigurati/sfridi-formato?${params}`
        );
        setSfridi(res.data || []);
      } catch (err: unknown) {
        setSfridi([]);
        setErrore(err instanceof Error && err.message ? err.message : 'Errore nel caricamento degli sfridi');
      }
      finally { setLoading(false); }
    };
    load();
  }, [societaId, formatoId]);

  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal" onClick={e => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">Sfridi — Formato {formatoNome}</h2>
          <button className="bop-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="bop-modal__body">
          {loading ? (
            <p className="bop-text-muted">Caricamento...</p>
          ) : errore ? (
            <div className="inline-error">Impossibile leggere gli sfridi: {errore}</div>
          ) : sfridi.length === 0 ? (
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">Nessuno sfrido configurato per il formato «{formatoNome}»</p>
              <p className="bop-empty-state__text bop-text-muted">
                Gli scaglioni si impostano in Archivi → Configurazioni Articoli → Sfridi, sul formato
                esatto dell'articolo (formati con nome simile, es. «120X120» e «120x120 CM», sono distinti).
                Intanto puoi scrivere la percentuale a mano nel campo sfrido.
              </p>
            </div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th className="bop-table__th">Da (qtà)</th>
                  <th className="bop-table__th">A (qtà)</th>
                  <th className="bop-table__th">Sfrido %</th>
                  <th className="bop-table__th bop-table__th--actions">Azione</th>
                </tr>
              </thead>
              <tbody>
                {sfridi.map(s => (
                  <tr key={s.id} className={`bop-table__row${s.sfrido === sfridoCorrente ? ' prec-sfrido-row--attivo' : ''}`}>
                    <td className="bop-table__td">{s.quantitaDa}</td>
                    <td className="bop-table__td">{s.quantitaA ?? '∞'}</td>
                    <td className="bop-table__td">
                      {s.sfrido === sfridoCorrente
                        ? <span className="bop-badge bop-badge--primary">{s.sfrido}%</span>
                        : <span>{s.sfrido}%</span>}
                    </td>
                    <td className="bop-table__td bop-table__td--actions">
                      <button className="bop-btn bop-btn-primary bop-btn-sm" onClick={() => onSeleziona(s.sfrido)}>Seleziona</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="bop-text-muted" style={{ fontSize: 'var(--bop-font-xs)', marginTop: 'var(--bop-spacing-3)' }}>
            Puoi anche inserire manualmente la percentuale nel campo Sfrido %
          </p>
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale Nuovo Articolo Pre-configurato ─────────────────────────────────────
function ModalNuovoPreconfigurato({
  societaId, onClose, onCreated,
}: {
  societaId: string;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [modelli, setModelli] = useState<ModelloOption[]>([]);
  const [loadingModelli, setLoadingModelli] = useState(true);
  const [modelloSelezionato, setModelloSelezionato] = useState<ModelloOption | null>(null);
  const [nome, setNome] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const params = societaId ? `?societaId=${societaId}` : '';
        // @api GET /api/articoli-preconfigurati/modelli?societaId -> [{id,codice,nome,articoloPadreId,...,componenti:[...]}]
        const res = await apiFetch<{ success: boolean; data: ModelloOption[] }>(
          `/api/articoli-preconfigurati/modelli${params}`
        );
        setModelli(res.data || []);
      } catch { /* silenzioso */ }
      finally { setLoadingModelli(false); }
    };
    load();
  }, [societaId]);

  const handleSelezionaModello = (m: ModelloOption) => {
    setModelloSelezionato(m);
    const nomeGenerato = m.descrizioneTemplate
      ? risolviTemplate(m.descrizioneTemplate, m.componenti)
      : m.nome;
    setNome(nomeGenerato);
    setStep(2);
  };

  const handleSalva = async () => {
    if (!nome.trim()) { showToast('Il nome è obbligatorio', 'error'); return; }
    if (!modelloSelezionato) { showToast('Seleziona un modello', 'error'); return; }
    setSaving(true);
    try {
      // @api POST /api/articoli-preconfigurati {societaId,nome,articoloPadreId,modelloId} -> {id,codice,nome}
      const res = await apiFetch<{ success: boolean; data: { id: number; codice: string; nome: string } }>(
        '/api/articoli-preconfigurati',
        {
          method: 'POST',
          body: JSON.stringify({
            societaId: Number(societaId),
            nome,
            articoloPadreId: modelloSelezionato.articoloPadreId,
            modelloId: modelloSelezionato.id,
          }),
        }
      );
      showToast(`Articolo ${res.data.codice} creato`, 'success');
      onCreated(res.data.id);
    } catch {
      showToast('Errore nella creazione', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--lg" onClick={e => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">Nuovo articolo pre-configurato</h2>
          <button className="bop-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="bop-modal__body">
          <div className="prec-step-indicator">
            <div className={`prec-step${step === 1 ? ' prec-step--attivo' : ' prec-step--completato'}`}>
              <span className="prec-step__num">{step > 1 ? '✓' : '1'}</span>
              Scegli modello
            </div>
            <div className={`prec-step${step === 2 ? ' prec-step--attivo' : ''}`}>
              <span className="prec-step__num">2</span>
              Definisci nome
            </div>
          </div>

          {step === 1 && (
            <>
              {loadingModelli ? (
                <p className="bop-text-muted">Caricamento modelli...</p>
              ) : modelli.length === 0 ? (
                <div className="bop-empty-state">
                  <span className="bop-empty-state__icon">📋</span>
                  <p className="bop-empty-state__text">Nessun modello disponibile — crea prima un modello di configurazione</p>
                </div>
              ) : (
                <div className="bop-table-wrapper">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th className="bop-table__th">Codice</th>
                        <th className="bop-table__th">Nome modello</th>
                        <th className="bop-table__th">Articolo padre</th>
                        <th className="bop-table__th bop-table__th--actions">Azione</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modelli.map(m => (
                        <tr key={m.id} className="bop-table__row">
                          <td className="bop-table__td"><span className="bop-badge bop-badge--info">{m.codice}</span></td>
                          <td className="bop-table__td bop-text-bold">{m.nome}</td>
                          <td className="bop-table__td">
                            <span className="bop-text-muted" style={{ fontSize: 'var(--bop-font-xs)' }}>{m.articoloPadreCodice}</span>
                            <br />
                            <span style={{ fontSize: 'var(--bop-font-sm)' }}>{m.articoloPadreNome}</span>
                          </td>
                          <td className="bop-table__td bop-table__td--actions">
                            <button className="bop-btn bop-btn-primary bop-btn-sm" onClick={() => handleSelezionaModello(m)}>
                              Usa modello
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {step === 2 && modelloSelezionato && (
            <>
              <div className="prec-modello-info">
                <div className="prec-modello-info__title">
                  <span className="bop-badge bop-badge--info" style={{ marginRight: 'var(--bop-spacing-2)' }}>{modelloSelezionato.codice}</span>
                  {modelloSelezionato.nome}
                </div>
                <div className="prec-modello-info__sub">
                  Articolo padre: <strong>{modelloSelezionato.articoloPadreCodice}</strong> — {modelloSelezionato.articoloPadreNome}
                  &nbsp;|&nbsp; UM: <strong>{modelloSelezionato.umVenditaSimbolo}</strong>
                  &nbsp;|&nbsp; {modelloSelezionato.componenti.length} componenti
                </div>
              </div>
              <div className="bop-form-row" style={{ marginTop: 'var(--bop-spacing-4)' }}>
                <div className="bop-field">
                  <label className="bop-field__label">Codice</label>
                  <input className="bop-field__input" value="PREC..." disabled />
                  <p className="bop-field__hint">Generato automaticamente</p>
                </div>
                <div className="bop-field" style={{ flex: 3 }}>
                  <label className="bop-field__label">Nome articolo <span className="bop-field__required">*</span></label>
                  <input
                    className="bop-field__input"
                    value={nome}
                    onChange={e => setNome(e.target.value)}
                    placeholder="Nome articolo pre-configurato..."
                  />
                  <p className="bop-field__hint">Pre-compilato dal template del modello — puoi modificarlo</p>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="bop-modal__footer">
          {step === 2 && (
            <button className="bop-btn bop-btn-ghost" onClick={() => setStep(1)}>← Indietro</button>
          )}
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          {step === 2 && (
            <button className="bop-btn bop-btn-primary" onClick={handleSalva} disabled={saving}>
              {saving ? 'Creazione...' : 'Crea articolo →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Vista Lista ───────────────────────────────────────────────────────────────
function VistaLista({
  societaId, listaSocieta, isUnica, setSocietaId, onEdit, onNuovo, onDuplica,
}: {
  societaId: string;
  listaSocieta: { id: number; nome: string }[];
  isUnica: boolean;
  setSocietaId: (id: string) => void;
  onEdit: (id: number) => void;
  onNuovo: () => void;
  onDuplica: (draft: DraftPreconfigurato) => void;
}) {
  const { can } = usePermesso();
  const [articoli, setArticoli] = useState<ArticoloRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroArticoloPadreId, setFiltroArticoloPadreId] = useState('');

  const carica = useCallback(async () => {
    if (!societaId) { setArticoli([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ societaId });
      // @api GET /api/articoli-preconfigurati?societaId&modelloId&articoloPadreId&q -> [{id,codice,nome,...}]
      const res = await apiFetch<{ success: boolean; data: ArticoloRow[] }>(
        `/api/articoli-preconfigurati?${params}`
      );
      setArticoli(res.data || []);
    } catch {
      showToast('Errore nel caricamento', 'error');
    } finally {
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => { carica(); }, [carica]);

  // Articoli padre univoci per il filtro
  const articoliPadreUnici = Array.from(
    new Map(articoli.map(a => [a.articoloPadreId, { id: a.articoloPadreId, codice: a.articoloPadreCodice, nome: a.articoloPadreNome }])).values()
  );

  const filtered = articoli.filter(a => {
    const matchText = !searchTerm ||
      a.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.codice.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.articoloPadreCodice.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.articoloPadreNome.toLowerCase().includes(searchTerm.toLowerCase());
    const matchPadre = !filtroArticoloPadreId || String(a.articoloPadreId) === filtroArticoloPadreId;
    return matchText && matchPadre;
  });

  const handleElimina = async (a: ArticoloRow) => {
    if (!window.confirm(`Eliminare l'articolo pre-configurato "${a.nome}"?`)) return;
    try {
      // @api DELETE /api/articoli-preconfigurati/:id -> {success:true}
      await apiFetch(`/api/articoli-preconfigurati/${a.id}`, { method: 'DELETE' });
      showToast('Articolo eliminato', 'success');
      carica();
    } catch {
      showToast("Errore nell'eliminazione", 'error');
    }
  };

  const handleDuplica = async (a: ArticoloRow) => {
    if (!societaId) return;
    try {
      // Carica l'articolo sorgente con tutti i componenti, poi apre la pagina
      // di dettaglio in modalità creazione con la bozza pre-popolata.
      // Nessuna POST viene fatta qui: il record verrà persistito a DB solo
      // quando l'utente cliccherà "Salva articolo" nella pagina di dettaglio.
      // @api GET /api/articoli-preconfigurati/:id -> {id,codice,nome,componenti:[...]}
      const res = await apiFetch<{ success: boolean; data: ArticoloDettaglio }>(
        `/api/articoli-preconfigurati/${a.id}`
      );
      const src = res.data;
      onDuplica({
        nome: `${src.nome} (copia)`,
        articoloPadreId: src.articoloPadreId,
        articoloPadreCodice: src.articoloPadreCodice,
        articoloPadreNome: src.articoloPadreNome,
        umVenditaSimbolo: src.umVenditaSimbolo,
        // Componenti senza id DB: saranno creati ex-novo al salvataggio.
        componenti: src.componenti.map(c => ({
          _key: crypto.randomUUID(),
          ordine: c.ordine,
          tipologia: c.tipologia,
          categoriaMerceologicaId: c.categoriaMerceologicaId,
          categoriaNome: c.categoriaNome,
          articoloId: c.articoloId,
          articoloCodice: c.articoloCodice,
          articoloDescrizione: c.articoloDescrizione,
          articoloUmVenditaSimbolo: c.articoloUmVenditaSimbolo,
          haFormato: c.haFormato,
          formatoId: c.formatoId,
          formatoNome: c.formatoNome,
          quantita: c.quantita,
          sfrido: c.sfrido,
          isPrincipale: c.isPrincipale,
          isInDescrizione: c.isInDescrizione,
        })),
      });
    } catch {
      showToast("Errore nella duplicazione dell'articolo", 'error');
    }
  };

  // Unità di misura presenti nei dati → opzioni del filtro enum
  const umOptions = useMemo(() => {
    const um = Array.from(new Set(articoli.map(a => a.umVenditaSimbolo).filter(Boolean))).sort();
    return um.map(u => ({ value: u, label: u }));
  }, [articoli]);

  const columns = useMemo<BopExcelColumn<ArticoloRow>[]>(() => [
    {
      key: 'codice',
      header: 'Codice',
      accessor: (a) => a.codice,
      filter: { type: 'text' },
      width: '140px',
      render: (a) => <span className="bop-badge bop-badge--info">{a.codice}</span>,
    },
    {
      key: 'nome',
      header: 'Nome',
      accessor: (a) => a.nome,
      filter: { type: 'text' },
      cellClassName: 'bop-prec__nome',
      render: (a) => a.nome,
    },
    {
      key: 'articoloPadre',
      header: 'Articolo padre',
      accessor: (a) => `${a.articoloPadreCodice} ${a.articoloPadreNome}`.trim(),
      filter: { type: 'text' },
      render: (a) => (
        <span className="bop-prec__padre">
          <span className="bop-prec__padre-codice">{a.articoloPadreCodice}</span>
          <span className="bop-prec__padre-nome">{a.articoloPadreNome}</span>
        </span>
      ),
    },
    {
      key: 'um',
      header: 'UM',
      accessor: (a) => a.umVenditaSimbolo,
      filter: { type: 'enum', options: umOptions },
      width: '90px',
      render: (a) => <span className="bop-badge bop-badge--neutral">{a.umVenditaSimbolo}</span>,
    },
    {
      key: 'nComponenti',
      header: 'Componenti',
      accessor: (a) => a.nComponenti,
      filter: { type: 'number' },
      width: '120px',
      cellClassName: 'bop-text-center',
      headerClassName: 'bop-text-center',
      render: (a) => <span className="bop-badge bop-badge--primary">{a.nComponenti}</span>,
    },
  ], [umOptions]);

  const emptyState = !societaId ? (
    <div className="bop-empty-state">
      <span className="bop-empty-state__icon">🏢</span>
      <p className="bop-empty-state__text">Seleziona una società per visualizzare gli articoli</p>
    </div>
  ) : (
    <div className="bop-empty-state">
      <span className="bop-empty-state__icon">📦</span>
      <p className="bop-empty-state__text">Nessun articolo pre-configurato trovato</p>
    </div>
  );

  return (
    <Layout>
      <div className="bop-page">
        <div className="bop-page__header">
          <div>
            <h1 className="bop-page__title">Articoli pre-configurati</h1>
            <p className="bop-page__subtitle">Articoli configurati pronti da usare nei preventivi</p>
          </div>
          <div className="bop-page__actions">
            {can('archivi.articoli_preconfigurati', 'edit') && (
              <button className="bop-btn bop-btn-primary" onClick={onNuovo} disabled={!societaId}>
                <PlusIcon /> Nuovo articolo
              </button>
            )}
          </div>
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <div className="bop-field" style={{ maxWidth: 280 }}>
              <label className="bop-field__label">Società</label>
              <select className="bop-field__select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
                <option value="">Tutte le società</option>
                {listaSocieta.map(s => <option key={s.id} value={String(s.id)}>{s.nome}</option>)}
              </select>
            </div>
          )}
          <div className="bop-field" style={{ maxWidth: 280 }}>
            <label className="bop-field__label">Articolo padre</label>
            <select className="bop-field__select" value={filtroArticoloPadreId} onChange={e => setFiltroArticoloPadreId(e.target.value)}>
              <option value="">Tutti</option>
              {articoliPadreUnici.map(a => <option key={a.id} value={String(a.id)}>{a.codice} — {a.nome}</option>)}
            </select>
          </div>
          <div className="bop-field" style={{ maxWidth: 300 }}>
            <label className="bop-field__label">Cerca</label>
            <input
              className="bop-field__input"
              placeholder="Codice, nome, articolo padre..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <BopExcelTable<ArticoloRow>
          data={filtered}
          loading={loading}
          rowKey={(a) => a.id}
          onRowClick={(a) => onEdit(a.id)}
          initialSort={{ key: 'codice', dir: 'asc' }}
          emptyState={emptyState}
          columns={columns}
          rowActions={(a) => (
            <BopRowActions
              actions={[
                { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => onEdit(a.id), variant: 'primary' },
                { icon: <RowIcon.Copy />, label: 'Duplica', onClick: () => handleDuplica(a), hidden: !can('archivi.articoli_preconfigurati', 'edit') },
                { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => handleElimina(a), variant: 'danger', hidden: !can('archivi.articoli_preconfigurati', 'delete') },
              ]}
            />
          )}
        />

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}

// ── Vista Dettaglio ───────────────────────────────────────────────────────────
function VistaDettaglio({
  articoloId, initialDraft, societaId, onBack, onCreated,
}: {
  articoloId: number | null;
  initialDraft?: DraftPreconfigurato;
  societaId: string;
  onBack: () => void;
  onCreated?: (id: number) => void;
}) {
  const { can } = usePermesso();
  const isCreate = articoloId === null;
  const [articolo, setArticolo] = useState<ArticoloDettaglio | null>(null);
  const [nome, setNome] = useState('');
  const [articoloPadre, setArticoloPadre] = useState<ArticoloPadreOption | null>(null);
  const [umVenditaSimbolo, setUmVenditaSimbolo] = useState('');
  const [componenti, setComponenti] = useState<ComponenteLocale[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isCreate);
  const [modalSceltaKey, setModalSceltaKey] = useState<string | null>(null);
  const [modalSfridoKey, setModalSfridoKey] = useState<string | null>(null);

  const carica = useCallback(async () => {
    if (isCreate) {
      // Modalità creazione: nessuna chiamata GET, lo stato viene popolato
      // dalla bozza in memoria (pre-popolata per "Duplica").
      if (initialDraft) {
        setNome(initialDraft.nome);
        setUmVenditaSimbolo(initialDraft.umVenditaSimbolo);
        setArticoloPadre({
          id: initialDraft.articoloPadreId,
          codice: initialDraft.articoloPadreCodice,
          descrizioneBase: initialDraft.articoloPadreNome,
          umVenditaSimbolo: initialDraft.umVenditaSimbolo,
        });
        setComponenti(
          initialDraft.componenti.map(c => ({
            ...c,
            _key: c._key || crypto.randomUUID(),
          }))
        );
      }
      setIsDirty(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // @api GET /api/articoli-preconfigurati/:id -> {id,codice,nome,componenti:[...]}
      const res = await apiFetch<{ success: boolean; data: ArticoloDettaglio }>(
        `/api/articoli-preconfigurati/${articoloId}`
      );
      const d = res.data;
      setArticolo(d);
      setNome(d.nome);
      setUmVenditaSimbolo(d.umVenditaSimbolo);
      setArticoloPadre({ id: d.articoloPadreId, codice: d.articoloPadreCodice, descrizioneBase: d.articoloPadreNome, umVenditaSimbolo: d.umVenditaSimbolo });
      setComponenti(d.componenti.map(c => ({ ...c, _key: crypto.randomUUID() })));
      setIsDirty(false);
    } catch {
      showToast('Errore nel caricamento', 'error');
    } finally {
      setLoading(false);
    }
  }, [articoloId, isCreate, initialDraft]);

  useEffect(() => { carica(); }, [carica]);

  const handleBack = () => {
    if (isDirty && !window.confirm('Hai modifiche non salvate. Continuare?')) return;
    onBack();
  };

  const handleSalva = async () => {
    if (!nome.trim()) { showToast('Il nome è obbligatorio', 'error'); return; }
    if (!articoloPadre) { showToast("L'articolo padre è obbligatorio", 'error'); return; }
    const compSenzaArticolo = componenti.filter(c => !c.articoloId);
    if (compSenzaArticolo.length > 0) {
      showToast(`${compSenzaArticolo.length} componente/i senza articolo scelto`, 'error');
      return;
    }
    setSaving(true);
    try {
      // In modifica l'id serve al backend per aggiornare il componente in loco
      // (le righe di ordini/preventivi lo referenziano); in creazione viene ignorato.
      const componentiPayload = componenti.map(({ _key, ...c }) => (isCreate ? { ...c, id: undefined } : c));

      if (isCreate) {
        // @api POST /api/articoli-preconfigurati {societaId,nome,articoloPadreId,componenti:[...]} -> {id,codice,nome}
        const res = await apiFetch<{ success: boolean; data: { id: number; codice: string; nome: string } }>(
          '/api/articoli-preconfigurati',
          {
            method: 'POST',
            body: JSON.stringify({
              societaId: Number(societaId),
              nome,
              articoloPadreId: articoloPadre.id,
              componenti: componentiPayload,
            }),
          }
        );
        showToast(`Articolo ${res.data.codice} creato`, 'success');
        setIsDirty(false);
        onCreated?.(res.data.id);
        return;
      }

      // @api PUT /api/articoli-preconfigurati/:id {nome,articoloPadreId,componenti:[...]} -> articolo completo
      const res = await apiFetch<{ success: boolean; data: ArticoloDettaglio }>(
        `/api/articoli-preconfigurati/${articoloId}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            nome,
            articoloPadreId: articoloPadre.id,
            componenti: componentiPayload,
          }),
        }
      );
      const d = res.data;
      setArticolo(d);
      setNome(d.nome);
      setUmVenditaSimbolo(d.umVenditaSimbolo);
      setArticoloPadre({ id: d.articoloPadreId, codice: d.articoloPadreCodice, descrizioneBase: d.articoloPadreNome, umVenditaSimbolo: d.umVenditaSimbolo });
      setComponenti(d.componenti.map(c => ({ ...c, _key: crypto.randomUUID() })));
      setIsDirty(false);
      showToast('Articolo salvato', 'success');
    } catch (err) {
      showToast(err instanceof Error && err.message ? err.message : 'Errore nel salvataggio', 'error');
    } finally {
      setSaving(false);
    }
  };

  const aggiornaNome = () => {
    const nuovo = risolviTemplate(
      '',
      componenti.map(c => ({ ordine: c.ordine, articoloDescrizione: c.articoloDescrizione, categoriaNome: c.categoriaNome }))
    );
    setNome(nuovo || nome);
    setIsDirty(true);
  };

  // Anteprima nome generato
  const anteprimaNome = componenti.length > 0
    ? componenti.filter(c => c.isPrincipale || c.isInDescrizione)
        .map(c => c.articoloDescrizione || c.categoriaNome || '?')
        .join(' · ')
    : '';

  const setArticoloSuComp = (key: string, a: ArticoloCercaOption) => {
    setComponenti(prev => prev.map(c =>
      c._key === key
        ? { ...c, articoloId: a.id, articoloCodice: a.codice, articoloDescrizione: a.descrizioneBase, articoloUmVenditaSimbolo: a.umVenditaSimbolo, haFormato: a.haFormato, formatoId: a.formatoId, formatoNome: a.formatoNome }
        : c
    ));
    setModalSceltaKey(null);
    setIsDirty(true);
  };

  const clearArticoloSuComp = (key: string) => {
    setComponenti(prev => prev.map(c =>
      c._key === key
        ? { ...c, articoloId: null, articoloCodice: '', articoloDescrizione: '', articoloUmVenditaSimbolo: '', haFormato: false, formatoId: null, formatoNome: null, sfrido: null }
        : c
    ));
    setIsDirty(true);
  };

  const setSfridoSuComp = (key: string, sfrido: number) => {
    setComponenti(prev => prev.map(c => c._key === key ? { ...c, sfrido } : c));
    setModalSfridoKey(null);
    setIsDirty(true);
  };

  const compModalScelta = modalSceltaKey ? componenti.find(c => c._key === modalSceltaKey) : null;
  const compModalSfrido = modalSfridoKey ? componenti.find(c => c._key === modalSfridoKey) : null;

  if (loading) {
    return (
      <Layout>
        <div className="bop-page">
          <div className="bop-empty-state"><span className="bop-empty-state__icon">⏳</span><p className="bop-empty-state__text">Caricamento...</p></div>
        </div>
      </Layout>
    );
  }

  if (!isCreate && !articolo) {
    return (
      <Layout>
        <div className="bop-page">
          <div className="bop-empty-state"><span className="bop-empty-state__icon">❌</span><p className="bop-empty-state__text">Articolo non trovato</p></div>
          <button className="bop-btn bop-btn-secondary" onClick={onBack}>← Torna alla lista</button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bop-page">

        {/* Header */}
        <div className="bop-page__header">
          <div>
            <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={handleBack} style={{ marginBottom: 'var(--bop-spacing-2)' }}>
              ← Torna agli articoli pre-configurati
            </button>
            <h1 className="bop-page__title">{nome || (isCreate ? 'Nuovo articolo pre-configurato' : '')}</h1>
            <p className="bop-page__subtitle">
              {isCreate ? (
                <span className="bop-badge bop-badge--warning" style={{ marginRight: 'var(--bop-spacing-2)' }}>Bozza non salvata</span>
              ) : (
                <span className="bop-badge bop-badge--info" style={{ marginRight: 'var(--bop-spacing-2)' }}>{articolo!.codice}</span>
              )}
              Articolo padre: <strong>{articoloPadre?.codice || '—'}</strong> — {articoloPadre?.descrizioneBase || '—'}
              &nbsp;|&nbsp; UM: <strong>{umVenditaSimbolo || '—'}</strong>
            </p>
          </div>
          <div className="bop-page__actions">
            <button className="bop-btn bop-btn-secondary" onClick={handleBack}>Annulla</button>
            {can('archivi.articoli_preconfigurati', 'edit') && (
              <button className="bop-btn bop-btn-primary" onClick={handleSalva} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva articolo'}
              </button>
            )}
          </div>
        </div>

        {/* Card Dati */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">Dati articolo</h3>
          </div>
          <div className="bop-card__body">
            <div className="bop-form-row">
              <div className="bop-field">
                <label className="bop-field__label">Codice</label>
                <input
                  className="bop-field__input"
                  value={isCreate ? 'PREC…' : articolo!.codice}
                  disabled
                />
                <p className="bop-field__hint">
                  {isCreate ? 'Generato automaticamente al salvataggio' : 'Generato automaticamente'}
                </p>
              </div>
              <div className="bop-field" style={{ flex: 3 }}>
                <label className="bop-field__label">Nome articolo <span className="bop-field__required">*</span></label>
                <input
                  className="bop-field__input"
                  value={nome}
                  onChange={e => { setNome(e.target.value); setIsDirty(true); }}
                />
                {anteprimaNome && (
                  <div className="prec-template-hint">
                    <span className="prec-template-hint__label">Generato da:</span>
                    <span>{anteprimaNome}</span>
                    <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={aggiornaNome}>Rigenera</button>
                  </div>
                )}
              </div>
            </div>
            <div className="bop-form-row" style={{ marginTop: 'var(--bop-spacing-4)' }}>
              <div className="bop-field" style={{ flex: 2 }}>
                <label className="bop-field__label">Articolo padre <span className="bop-field__required">*</span></label>
                <AutocompleteArticoloPadre
                  societaId={societaId}
                  selected={articoloPadre}
                  onSelect={a => { setArticoloPadre(a); setUmVenditaSimbolo(a.umVenditaSimbolo); setIsDirty(true); }}
                  onClear={() => { setArticoloPadre(null); setUmVenditaSimbolo(''); setIsDirty(true); }}
                />
                <p className="bop-field__hint">Solo articoli di tipologia PRODOTTO_CONFIGURATO</p>
              </div>
              <div className="bop-field">
                <label className="bop-field__label">UM di vendita</label>
                <input className="bop-field__input" value={umVenditaSimbolo} disabled />
                <p className="bop-field__hint">Ereditata dall'articolo padre</p>
              </div>
            </div>
          </div>
        </div>

        {/* Card Componenti */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">
              Componenti
              <span className="bop-badge bop-badge--primary" style={{ marginLeft: 'var(--bop-spacing-2)' }}>{componenti.length}</span>
            </h3>
          </div>
          <div className="bop-card__body" style={{ padding: 0 }}>
            <table className="bop-table">
              <thead>
                <tr>
                  <th className="bop-table__th" style={{ width: 40 }}>#</th>
                  <th className="bop-table__th">Tipologia</th>
                  <th className="bop-table__th">Categoria</th>
                  <th className="bop-table__th">Articolo scelto</th>
                  <th className="bop-table__th bop-text-center" style={{ width: 90 }}>U.M. vendita</th>
                  <th className="bop-table__th" style={{ width: 120 }}>Qtà / UM</th>
                  <th className="bop-table__th" style={{ width: 130 }}>
                    Sfrido %
                    <Tooltip text="Visibile solo per articoli fisici con formato configurato" />
                  </th>
                  <th className="bop-table__th bop-text-center" style={{ width: 100 }}>Principale</th>
                  <th className="bop-table__th bop-text-center" style={{ width: 100 }}>In descriz.</th>
                </tr>
              </thead>
              <tbody>
                {componenti.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="bop-table__empty">
                      <div className="bop-empty-state"><span className="bop-empty-state__icon">🧩</span><p className="bop-empty-state__text">Nessun componente</p></div>
                    </td>
                  </tr>
                ) : componenti.map(c => (
                  <tr key={c._key} className="bop-table__row">
                    <td className="bop-table__td bop-text-muted" style={{ fontWeight: 600 }}>{c.ordine}</td>
                    <td className="bop-table__td">
                      <span className={`bop-badge ${c.tipologia === 'LAVORAZIONE_PRESTAZIONE' ? 'bop-badge--warning' : 'bop-badge--info'}`}>
                        {c.tipologia.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="bop-table__td bop-text-bold">{c.categoriaNome}</td>
                    <td className="bop-table__td">
                      {c.articoloId ? (
                        <span className="prec-articolo-badge">
                          <strong>{c.articoloCodice}</strong> — {c.articoloDescrizione}
                          {can('archivi.articoli_preconfigurati', 'edit') && (
                            <button className="prec-articolo-badge__remove" onClick={() => clearArticoloSuComp(c._key)} title="Rimuovi">✕</button>
                          )}
                        </span>
                      ) : (
                        can('archivi.articoli_preconfigurati', 'edit') ? (
                          <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setModalSceltaKey(c._key)}>
                            <SearchIcon /> Scegli articolo
                          </button>
                        ) : (
                          <span className="bop-badge bop-badge--warning">Non scelto</span>
                        )
                      )}
                    </td>
                    <td className="bop-table__td bop-text-center">
                      {c.articoloUmVenditaSimbolo
                        ? <span className="bop-badge bop-badge--neutral">{c.articoloUmVenditaSimbolo}</span>
                        : <span className="bop-text-muted">—</span>}
                    </td>
                    <td className="bop-table__td">
                      {can('archivi.articoli_preconfigurati', 'edit') ? (
                        <input
                          className="bop-field__input"
                          type="number"
                          step="0.001"
                          min="0"
                          value={c.quantita ?? ''}
                          onChange={e => {
                            const v = e.target.value === '' ? null : Number(e.target.value);
                            setComponenti(prev => prev.map(x => x._key === c._key ? { ...x, quantita: v } : x));
                            setIsDirty(true);
                          }}
                          style={{ width: 80 }}
                        />
                      ) : (
                        <span>{c.quantita ?? '—'} {umVenditaSimbolo}</span>
                      )}
                    </td>
                    <td className="bop-table__td">
                      {/* Sfrido solo sulla merce con formato; un valore già salvato
                          altrove resta visibile perché passa ai preventivi e ne
                          altera il prezzo. */}
                      {isTipologiaMerce(c.tipologia) && c.haFormato ? (
                        <div className="prec-sfrido-cell">
                          {can('archivi.articoli_preconfigurati', 'edit') ? (
                            <>
                              <input
                                className="bop-field__input prec-sfrido-input"
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                value={c.sfrido ?? ''}
                                placeholder="0"
                                onChange={e => {
                                  const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                  setComponenti(prev => prev.map(x => x._key === c._key ? { ...x, sfrido: v } : x));
                                  setIsDirty(true);
                                }}
                              />
                              <button
                                className="bop-btn bop-btn-ghost bop-btn-sm"
                                title="Scegli da tabella sfridi"
                                onClick={() => setModalSfridoKey(c._key)}
                                disabled={!c.formatoId}
                              >🔍</button>
                            </>
                          ) : (
                            <span>{c.sfrido !== null ? `${c.sfrido}%` : '—'}</span>
                          )}
                        </div>
                      ) : c.sfrido != null ? (
                        <div className="prec-sfrido-cell">
                          <span
                            className="bop-text-warning"
                            title="Lo sfrido è previsto solo sulla merce con formato, ma questo valore è salvato e passa ai preventivi"
                          >
                            {formatNumber(c.sfrido)}% (sfrido non previsto)
                          </span>
                          {can('archivi.articoli_preconfigurati', 'edit') && (
                            <button
                              className="bop-btn bop-btn-ghost bop-btn-sm"
                              title="Togli lo sfrido"
                              onClick={() => {
                                setComponenti(prev => prev.map(x => x._key === c._key ? { ...x, sfrido: null } : x));
                                setIsDirty(true);
                              }}
                            >✕</button>
                          )}
                        </div>
                      ) : (
                        <span className="bop-text-muted">—</span>
                      )}
                    </td>
                    <td className="bop-table__td bop-text-center">
                      {c.isPrincipale ? <span className="bop-badge bop-badge--success">✓ Sì</span> : <span className="bop-badge bop-badge--neutral">No</span>}
                    </td>
                    <td className="bop-table__td bop-text-center">
                      {c.isInDescrizione ? <span className="bop-badge bop-badge--info">{`{comp_${c.ordine}}`}</span> : <span className="bop-text-muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* Modali */}
      {modalSceltaKey && compModalScelta && (
        <ModalSceltaArticolo
          societaId={societaId}
          tipologia={compModalScelta.tipologia}
          categoriaMerceologicaId={compModalScelta.categoriaMerceologicaId}
          categoriaNome={compModalScelta.categoriaNome}
          onClose={() => setModalSceltaKey(null)}
          onSeleziona={a => setArticoloSuComp(modalSceltaKey, a)}
        />
      )}

      {modalSfridoKey && compModalSfrido && compModalSfrido.formatoId && (
        <ModalSfridoFormato
          societaId={societaId}
          formatoId={compModalSfrido.formatoId}
          formatoNome={compModalSfrido.formatoNome || ''}
          sfridoCorrente={compModalSfrido.sfrido}
          onClose={() => setModalSfridoKey(null)}
          onSeleziona={s => setSfridoSuComp(modalSfridoKey, s)}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────
export default function ArticoliPreconfiguratiPage() {
  const { societaId, setSocietaId, listaSocieta, loading, isUnica } = useFiltroSocieta();
  const [view, setView] = useState<'list' | 'detail' | 'create'>('list');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<DraftPreconfigurato | null>(null);
  const [showModalNuovo, setShowModalNuovo] = useState(false);

  useEffect(() => {
    setView('list');
    setSelectedId(null);
    setDraft(null);
  }, [societaId]);

  if (loading) {
    return (
      <Layout>
        <div className="bop-page">
          <div className="bop-empty-state"><p className="bop-empty-state__text">Caricamento...</p></div>
        </div>
      </Layout>
    );
  }

  if (view === 'detail' && selectedId !== null) {
    return (
      <VistaDettaglio
        articoloId={selectedId}
        societaId={societaId}
        onBack={() => { setView('list'); setSelectedId(null); }}
      />
    );
  }

  if (view === 'create' && draft) {
    return (
      <VistaDettaglio
        articoloId={null}
        initialDraft={draft}
        societaId={societaId}
        onBack={() => { setView('list'); setDraft(null); }}
        onCreated={id => { setDraft(null); setSelectedId(id); setView('detail'); }}
      />
    );
  }

  return (
    <>
      <VistaLista
        societaId={societaId}
        listaSocieta={listaSocieta}
        isUnica={isUnica}
        setSocietaId={setSocietaId}
        onEdit={id => { setSelectedId(id); setView('detail'); }}
        onNuovo={() => setShowModalNuovo(true)}
        onDuplica={d => { setDraft(d); setView('create'); }}
      />
      {showModalNuovo && (
        <ModalNuovoPreconfigurato
          societaId={societaId}
          onClose={() => setShowModalNuovo(false)}
          onCreated={id => { setShowModalNuovo(false); setSelectedId(id); setView('detail'); }}
        />
      )}
    </>
  );
}
